import * as Api from '@ViewModels';

export type LayoutBoxHandle = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

export const LAYOUT_BOX_HANDLES: LayoutBoxHandle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

export const MIN_BOX_SIDE = 16;

export const clampToCanvas = (
	box: Api.IGoogleReviewPostLayoutBox,
	canvas: Api.IGoogleReviewPostCanvas
): Api.IGoogleReviewPostLayoutBox => {
	const width = Math.min(Math.max(Math.round(box.width), MIN_BOX_SIDE), canvas.width);
	const height = Math.min(Math.max(Math.round(box.height), MIN_BOX_SIDE), canvas.height);

	return {
		...box,
		width,
		height,
		x: Math.min(Math.max(Math.round(box.x), 0), canvas.width - width),
		y: Math.min(Math.max(Math.round(box.y), 0), canvas.height - height),
	};
};

export const moveBox = (
	box: Api.IGoogleReviewPostLayoutBox,
	dx: number,
	dy: number,
	canvas: Api.IGoogleReviewPostCanvas
): Api.IGoogleReviewPostLayoutBox => clampToCanvas({ ...box, x: box.x + dx, y: box.y + dy }, canvas);

export const resizeBox = (
	box: Api.IGoogleReviewPostLayoutBox,
	handle: LayoutBoxHandle,
	dx: number,
	dy: number,
	canvas: Api.IGoogleReviewPostCanvas
): Api.IGoogleReviewPostLayoutBox => {
	const right = Math.min(handle.includes('e') ? box.x + box.width + dx : box.x + box.width, canvas.width);
	const bottom = Math.min(handle.includes('s') ? box.y + box.height + dy : box.y + box.height, canvas.height);
	const left = Math.max(handle.includes('w') ? Math.min(box.x + dx, right - MIN_BOX_SIDE) : box.x, 0);
	const top = Math.max(handle.includes('n') ? Math.min(box.y + dy, bottom - MIN_BOX_SIDE) : box.y, 0);

	return {
		...box,
		x: Math.round(left),
		y: Math.round(top),
		width: Math.round(Math.max(right - left, MIN_BOX_SIDE)),
		height: Math.round(Math.max(bottom - top, MIN_BOX_SIDE)),
	};
};

export interface StoredTemplateRef {
	key?: string;
	revision?: number;
}

export const isSameTemplate = (
	stored: StoredTemplateRef | undefined,
	template: Api.IGoogleReviewPostTemplateDocument
): boolean => !!stored?.key && stored.key === template.key && stored.revision === template.revision;

export const resolveLayout = (
	template: Api.IGoogleReviewPostTemplateDocument,
	stored: Api.IGoogleReviewPostLayout | undefined,
	storedTemplate: StoredTemplateRef | undefined,
	override: Partial<Api.IGoogleReviewPostLayout> | null
): Api.IGoogleReviewPostLayout => {
	const defaults = template.defaultLayout;
	const usable = isSameTemplate(storedTemplate, template) ? stored : undefined;
	const merged = { ...defaults, ...(usable ?? {}), ...(override ?? {}) };

	return {
		text: clampToCanvas(merged.text ?? defaults.text, template.canvas),
		stars: clampToCanvas(merged.stars ?? defaults.stars, template.canvas),
		logo: clampToCanvas(merged.logo ?? defaults.logo, template.canvas),
		author: clampToCanvas(merged.author ?? defaults.author, template.canvas),
	};
};
